import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import withLoader from '../../components/Loader/withLoader.js';
import { get } from 'lodash-es';

// Actions
import { actions as routingActions } from '../../ducks/routing.js';
import { actions as authActions } from '../../ducks/auth.js';
import { actions as appActions } from '../../ducks/app.js';
import { actions as customerActions } from '../../ducks/customer.js';

// Components
import AccountDetailsLayout from '../../components/AccountDetailsLayout/AccountDetailsLayout';
import CardSpacer from '../../components/CardSpacer/CardSpacer.js';
import BalanceCard from '../../components/AccountsSection/BalanceCard/BalanceCard';
import HelpCard from '../../components/HelpCard/HelpCard.js';
import ReplaceContact from '../../components/Replace/ReplaceContact/ReplaceContact';

// Icons
import AccountIcon from '@material-ui/icons/AccountBalanceOutlined';
import InfoIcon from '@material-ui/icons/InfoOutlined';
import MoneyIcon from '@material-ui/icons/AttachMoney';

import {
  getString,
  stringToDate,
  stringToDateShort,
  stringToCurrency,
} from '../../helpers/stringHelpers.js';
import { getLoadedStatus } from '../../helpers/loaderHelpers';
import { isFeatureDisabled } from '../../helpers/outageHelpers';
import analyticsApi from '../../api/analytics.js';
import constants from '../../constants';

const stringsComponent = 'accounts';

class AccountDetails extends React.Component {
  componentDidMount() {
    const { setAppBarTitle, accountType } = this.props;

    setAppBarTitle(
      getString(
        stringsComponent,
        accountType == 'maintenance'
          ? 'REPAIRS_ACCOUNT_TITLE'
          : 'RENT_ACCOUNT_TITLE'
      )
    );
  }

  shouldComponentUpdate(nextProps) {
    // Only rerender if not in a unloaded status.
    if (getLoadedStatus(this.getLoadedStatuses(nextProps)) !== 'UNLOADED') {
      return true;
    }
    return false;
  }

  getLoadedStatuses(props = this.props) {
    const { tenancy, account, transactions, accountType } = props;
    const loaded = [
      tenancy.loaded,
      account.loaded,
      transactions[accountType].loaded,
    ];
    return loaded;
  }

  replaceRentChargeTemplateVars(
    message,
    nextPaymentAmount,
    currentChargeAmount,
    currentChargeStartDate
  ) {
    return message
      .replace('$NextPaymentAmount', stringToCurrency(nextPaymentAmount))
      .replace('$CurrentChargeAmount', stringToCurrency(currentChargeAmount))
      .replace(
        '$CurrentChargeStartDate',
        stringToDateShort(currentChargeStartDate)
      );
  }

  render() {
    const {
      signout,
      getTransactions,
      tenancy,
      account,
      inOutage,
      outageFrom,
      outageTo,
      outageMessage,
      accountType,
      goAccounts,
      goArrangementDetails,
      goArrangementCreate,
      goAccountTransactions,
      toggleContactModal,
      onLoadComplete,
      roles,
    } = this.props;

    const tenancyData = tenancy.data;
    const accountData = account.data;

    const accountLoaded =
      accountData.hasOwnProperty('accounts') &&
      accountData.accounts.hasOwnProperty(accountType);

    const balanceAmount = accountLoaded
      ? Number(accountData.accounts[accountType].accountBalance)
      : 0;

    const rentBalanceAmount =
      accountData.hasOwnProperty('accounts') &&
      accountData.accounts.hasOwnProperty('rent')
        ? Number(accountData.accounts.rent.accountBalance)
        : 0;

    const rentBalance = (
      <BalanceCard
        title={
          accountType == 'rent'
            ? getString(stringsComponent, 'CURRENT_RENT_TITLE')
            : getString(stringsComponent, 'CURRENT_REPAIRS_TITLE')
        }
        balance={balanceAmount}
        label={
          getString(stringsComponent, 'DATE_LABEL') +
          constants.TEXT_SEPERATOR +
          stringToDate(new Date())
        }
        paymentReference={
          accountLoaded
            ? getString(stringsComponent, 'PAYMENT_REFERENCE') +
              constants.TEXT_SEPERATOR +
              accountData.accounts[accountType].paymentReference
            : null
        }
        icon={<AccountIcon />}
        helpText={portalContent.DASH_RENT}
        actionLabel={getString(stringsComponent, 'VIEW_TRANSACTIONS_LABEL')}
        action={() => {
          goAccountTransactions(accountType);
        }}
        actionTwo={
          get(accountData, `accounts.${accountType}.arrangements[0]`, null)
            ? () => goArrangementDetails(accountType)
            : null
        }
        actionTwoLabel={getString(stringsComponent, 'VIEW_ARRANGEMENTS_LABEL')}
        gaEvent={() =>
          analyticsApi.event(
            'Help',
            'Click',
            accountType === 'rent' ? 'Rent Balance' : 'Maintenance Balance'
          )
        }
        showArrangementAction={
          balanceAmount != 0 &&
          !get(accountData, `accounts.${accountType}.arrangements`, false)
        }
        isMaintenanceBalance={accountType == 'maintenance'}
        hasMaintenanceArrangement={
          accountType == 'rent' &&
          Boolean(get(accountData, 'accounts.maintenance.arrangements', false))
        }
        hasRentArrangementOrDebt={
          accountType == 'maintenance' &&
          (Boolean(get(accountData, 'accounts.rent.arrangements[0]', false)) ||
            rentBalanceAmount < 0)
        }
        arrangementFeatureDisabled={isFeatureDisabled(
          'ARRANGEMENTS_WRITE',
          tenancyData.area
        )}
        arrangementCreateAction={() => goArrangementCreate(accountType)}
        toggleContactModal={toggleContactModal}
        customerRoles={roles}
      />
    );

    const help = portalGuidances.find(
      (guidance) => guidance.Title === stringsComponent + '_' + accountType
    ) && (
      <HelpCard
        title={getString('help', 'TITLE')}
        subheading={getString('help', 'SUBHEADING')}
        content={
          portalGuidances.find(
            (guidance) =>
              guidance.Title === stringsComponent + '_' + accountType
          ).Content
        }
        contact={
          portalGuidances.find(
            (guidance) =>
              guidance.Title === stringsComponent + '_' + accountType
          ).Contact
        }
        faqs={
          portalGuidances.find(
            (guidance) =>
              guidance.Title === stringsComponent + '_' + accountType
          ).FAQs
        }
        icon={<InfoIcon />}
        toggleContactModal={toggleContactModal}
        gaEvent={analyticsApi.event}
      />
    );

    const nextPaymentAmount =
      accountLoaded &&
      accountData.accounts[accountType].hasOwnProperty('nextRentChargeAmount')
        ? Number(accountData.accounts[accountType].nextRentChargeAmount)
        : 0;

    const nextRentPayment =
      accountType == 'rent' ? (
        <div>
          <BalanceCard
            title={
              accountType == 'rent'
                ? getString(stringsComponent, 'NEXT_RENT_PAYMENT_TITLE')
                : getString(stringsComponent, 'NEXT_REPAIRS_PAYMENT_TITLE')
            }
            balance={nextPaymentAmount}
            label={
              accountLoaded &&
              accountData.accounts[accountType].hasOwnProperty(
                'nextRentChargeDate'
              )
                ? getString(stringsComponent, 'DUE_PAYMENT_LABEL') +
                  constants.TEXT_SEPERATOR +
                  stringToDate(
                    accountData.accounts[accountType].nextRentChargeDate
                  )
                : ''
            }
            icon={<MoneyIcon />}
          />
          <CardSpacer />
        </div>
      ) : null;

    const currentRentChargeAmount = accountLoaded
      ? Number(accountData.accounts[accountType].currentRentChargeAmount)
      : 0;

    const currentRentChargeStartDate =
      accountLoaded &&
      accountData.accounts[accountType].hasOwnProperty('currentRentChargeDate')
        ? accountData.accounts[accountType].currentRentChargeDate
        : null;

    const currentRentChargeReviewData =
      accountLoaded &&
      accountData.accounts[accountType].hasOwnProperty(
        'currentRentChargeReviewDate'
      )
        ? stringToDate(
            accountData.accounts[accountType].currentRentChargeReviewDate
          )
        : null;

    const currentRentChargeFrequency = accountLoaded
      ? accountData.accounts[accountType].currentRentChargeFrequency
      : null;

    const currentCharge = currentRentChargeAmount ? (
      <div>
        <BalanceCard
          title={getString(stringsComponent, 'CURRENT_CHARGE_TITLE')}
          balance={currentRentChargeAmount}
          label={
            currentRentChargeStartDate
              ? getString(stringsComponent, 'CURRENT_CHARGE_LABEL') +
                constants.TEXT_SEPERATOR +
                stringToDate(currentRentChargeStartDate)
              : ''
          }
          icon={<MoneyIcon />}
          actionLabel={
            currentRentChargeReviewData
              ? getString(stringsComponent, 'CURRENT_CHARGE_REVIEW_LABEL') +
                constants.TEXT_SEPERATOR +
                currentRentChargeReviewData
              : ''
          }
          showArrangementAction={currentRentChargeAmount != nextPaymentAmount}
          customArrangementMessage={this.replaceRentChargeTemplateVars(
            currentRentChargeAmount > nextPaymentAmount
              ? portalContent.ACCOUNTS_RENT_INCREASE
              : portalContent.ACCOUNTS_RENT_DECREASE,
            nextPaymentAmount,
            currentRentChargeAmount,
            currentRentChargeStartDate
          )}
          gaEvent={() =>
            analyticsApi.event('Help', 'Click', 'Current Rent Change')
          }
          paymentReference={
            currentRentChargeFrequency
              ? `${getString(
                  stringsComponent,
                  'PAYMENT_FREQUENCY'
                )} | ${currentRentChargeFrequency}`
              : null
          }
          toggleContactModal={toggleContactModal}
        />
        <CardSpacer />
      </div>
    ) : null;

    const WrappedAccountDetailsLayout = withLoader(AccountDetailsLayout);

    return (
      <WrappedAccountDetailsLayout
        currentBalance={rentBalance}
        help={help}
        balanceSectionTitle={getString(
          stringsComponent,
          'BALANCE_SECTION_TITLE'
        )}
        paymentSectionTitle={getString(
          stringsComponent,
          'PAYMENT_SECTION_TITLE'
        )}
        nextPayment={nextRentPayment}
        currentCharge={currentCharge}
        backAction={goAccounts}
        loaded={this.getLoadedStatuses()}
        onRefresh={getTransactions}
        onError={signout}
        inOutage={inOutage}
        outageFrom={outageFrom}
        outageTo={outageTo}
        outageMessage={
          outageMessage && (
            <ReplaceContact
              content={outageMessage}
              action={toggleContactModal}
              isLink={true}
            />
          )
        }
        failureText={
          <ReplaceContact
            content={portalContent.APP_ERROR_MESSAGE}
            action={toggleContactModal}
            isLink={true}
          />
        }
        notFoundText={portalContent.APP_NOT_FOUND_MESSAGE}
        onLoadComplete={onLoadComplete}
        headingTitle={
          accountType == 'rent'
            ? getString(stringsComponent, 'RENT_DETAILS_TITLE')
            : getString(stringsComponent, 'REPAIRS_DETAILS_TITLE')
        }
      />
    );
  }
}

const mapStateToProps = (state) => ({
  tenancy: state.customer.tenancy,
  account: state.customer.account,
  transactions: state.customer.transactions,
  appointments: state.customer.appointments,
  inOutage: state.app.inOutage,
  outageFrom: state.app.outageFrom,
  outageTo: state.app.outageTo,
  outageMessage: state.app.outageMessage,
  accountType: state.app.accountType,
  customerName: `${state.auth.firstName} ${state.auth.lastName}`,
  roles: state.auth.roles,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      ...routingActions,
      ...appActions,
      ...authActions,
      ...customerActions,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(AccountDetails);
